import { useMemo, useState } from 'react';
import { HardDrive } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { SortableTH, Table, TBody, TD, THead, TR } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState } from '@/components/data/EmptyState';
import { useToast } from '@/components/ui/toast';
import { useManifest, useManifestSummary } from '@/hooks/useManifest';
import { useTableSort } from '@/lib/useTableSort';
import { formatBytes, formatNumber, formatRelative } from '@/lib/utils';
import { PROVIDERS, allSports } from '@/config/providers';

export function StorageRoute() {
  const toast = useToast();
  const [provider, setProvider] = useState('');
  const [sport, setSport] = useState('');
  const [endpointType, setEndpointType] = useState('');
  const [search, setSearch] = useState('');

  const summary = useManifestSummary();
  const manifest = useManifest({
    provider: provider || undefined,
    sport: sport || undefined,
    endpointType: endpointType || undefined,
    limit: 200,
  });

  const endpointTypes = useMemo(() => {
    const set = new Set<string>();
    for (const r of summary.data ?? []) {
      if (provider && r.provider !== provider) continue;
      set.add(r.endpoint_type);
    }
    return [...set].sort();
  }, [summary.data, provider]);

  const filteredObjects = useMemo(() => {
    if (!search) return manifest.data ?? [];
    const needle = search.toLowerCase();
    return (manifest.data ?? []).filter((m) => m.s3_key.toLowerCase().includes(needle));
  }, [manifest.data, search]);

  // Pre-filtered summary rows for the roll-up table. Memoized once so the
  // table body, the empty-state guard, and the totals/StatPills all read
  // from a single source of truth.
  const filteredSummary = useMemo(() => {
    return (summary.data ?? []).filter(
      (r) =>
        (!provider || r.provider === provider) &&
        (!sport || r.sport === sport) &&
        (!endpointType || r.endpoint_type === endpointType),
    );
  }, [summary.data, provider, sport, endpointType]);

  // Roll-up table: every column sortable; bytes sorts by the raw byte count,
  // not the formatted "3 MB" string. No initial key — the view's natural order
  // is preserved on load.
  const summarySort = useTableSort(filteredSummary, {
    accessors: {
      object_count: (r) => Number(r.object_count),
      bytes_total: (r) => Number(r.bytes_total),
    },
  });

  // Recent-objects table sorts the loaded ≤200-row slice. Bytes sorts numerically.
  const objectsSort = useTableSort(filteredObjects, {
    accessors: {
      response_bytes: (m) => (m.response_bytes == null ? null : Number(m.response_bytes)),
    },
  });

  const totals = useMemo(() => {
    // Derived from filteredSummary so the filter predicate has one
    // canonical source. Previously this memo duplicated the three-way
    // provider/sport/endpointType check, which drifted if anyone updated
    // one site without the other.
    return filteredSummary.reduce(
      (acc, r) => {
        acc.objects += Number(r.object_count) || 0;
        acc.bytes += Number(r.bytes_total) || 0;
        return acc;
      },
      { objects: 0, bytes: 0 },
    );
  }, [filteredSummary]);

  return (
    <>
      <PageHeader
        title="S3 storage layer"
        description="Every object written to the revo-sports-vault bucket is registered in harvest.manifest. This page is the operator-facing index into that catalog."
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle>Objects</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {summary.isLoading || summary.isError ? '—' : formatNumber(totals.objects)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle>Bytes</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {summary.isLoading || summary.isError ? '—' : formatBytes(totals.bytes)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle>Avg object size</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {summary.isLoading || summary.isError || totals.objects === 0
                ? '—'
                : formatBytes(totals.bytes / totals.objects)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:space-y-0">
          <div>
            <CardTitle>Provider / sport / endpoint roll-up</CardTitle>
            <CardDescription>From the harvest.v_manifest_summary view.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={provider} onChange={(e) => { setProvider(e.target.value); setEndpointType(''); }} className="w-36">
              <option value="">All providers</option>
              {PROVIDERS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </Select>
            <Select value={sport} onChange={(e) => setSport(e.target.value)} className="w-36">
              <option value="">All sports</option>
              {allSports().map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
            <Select value={endpointType} onChange={(e) => setEndpointType(e.target.value)} className="w-44">
              <option value="">All endpoints</option>
              {endpointTypes.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {summary.isError ? (
            <ErrorState error={summary.error} />
          ) : summary.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : !filteredSummary.length ? (
            <EmptyState
              icon={HardDrive}
              title="No manifest rows yet"
              description="Either harvest.manifest is empty or the current filters exclude every row. Clear the filters or run a harvest to populate it."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <SortableTH label="Provider" sortKey="provider" active={summarySort.sortKey === 'provider'} direction={summarySort.sortDir} onSort={summarySort.toggle} />
                  <SortableTH label="Sport" sortKey="sport" active={summarySort.sortKey === 'sport'} direction={summarySort.sortDir} onSort={summarySort.toggle} />
                  <SortableTH label="Endpoint" sortKey="endpoint_type" active={summarySort.sortKey === 'endpoint_type'} direction={summarySort.sortDir} onSort={summarySort.toggle} />
                  <SortableTH label="Objects" sortKey="object_count" active={summarySort.sortKey === 'object_count'} direction={summarySort.sortDir} onSort={summarySort.toggle} align="right" className="text-right" />
                  <SortableTH label="Bytes" sortKey="bytes_total" active={summarySort.sortKey === 'bytes_total'} direction={summarySort.sortDir} onSort={summarySort.toggle} align="right" className="text-right" />
                  <SortableTH label="First" sortKey="first_collected_at" active={summarySort.sortKey === 'first_collected_at'} direction={summarySort.sortDir} onSort={summarySort.toggle} />
                  <SortableTH label="Last" sortKey="last_collected_at" active={summarySort.sortKey === 'last_collected_at'} direction={summarySort.sortDir} onSort={summarySort.toggle} />
                </TR>
              </THead>
              <TBody>
                {summarySort.sorted
                  .map((r) => (
                    <TR key={`${r.provider}-${r.sport}-${r.endpoint_type}`}>
                      <TD className="capitalize">{r.provider}</TD>
                      <TD className="capitalize">{r.sport}</TD>
                      <TD className="font-mono text-xs">{r.endpoint_type}</TD>
                      <TD className="text-right tabular-nums">{formatNumber(r.object_count)}</TD>
                      <TD className="text-right tabular-nums">{formatBytes(r.bytes_total)}</TD>
                      <TD>{formatRelative(r.first_collected_at)}</TD>
                      <TD>{formatRelative(r.last_collected_at)}</TD>
                    </TR>
                  ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:space-y-0">
          <div>
            <CardTitle>Recent objects</CardTitle>
            <CardDescription>200 most recent manifest rows · click an S3 key to copy.</CardDescription>
          </div>
          <Input
            placeholder="Filter by S3 key fragment…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
        </CardHeader>
        <CardContent>
          {manifest.isError ? (
            <ErrorState error={manifest.error} />
          ) : manifest.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : filteredObjects.length ? (
            <Table>
              <THead>
                <TR>
                  <SortableTH label="S3 key" sortKey="s3_key" active={objectsSort.sortKey === 's3_key'} direction={objectsSort.sortDir} onSort={objectsSort.toggle} />
                  <SortableTH label="Provider" sortKey="provider" active={objectsSort.sortKey === 'provider'} direction={objectsSort.sortDir} onSort={objectsSort.toggle} />
                  <SortableTH label="Sport" sortKey="sport" active={objectsSort.sortKey === 'sport'} direction={objectsSort.sortDir} onSort={objectsSort.toggle} />
                  <SortableTH label="Endpoint" sortKey="endpoint_type" active={objectsSort.sortKey === 'endpoint_type'} direction={objectsSort.sortDir} onSort={objectsSort.toggle} />
                  <SortableTH label="Status" sortKey="http_status" active={objectsSort.sortKey === 'http_status'} direction={objectsSort.sortDir} onSort={objectsSort.toggle} />
                  <SortableTH label="Bytes" sortKey="response_bytes" active={objectsSort.sortKey === 'response_bytes'} direction={objectsSort.sortDir} onSort={objectsSort.toggle} align="right" className="text-right" />
                  <SortableTH label="Collected" sortKey="collected_at" active={objectsSort.sortKey === 'collected_at'} direction={objectsSort.sortDir} onSort={objectsSort.toggle} />
                </TR>
              </THead>
              <TBody>
                {objectsSort.sorted.map((m) => (
                  <TR key={m.manifest_id}>
                    <TD>
                      <button
                        className="max-w-md truncate text-left font-mono text-[11px] text-foreground/80 hover:text-foreground"
                        onClick={() => {
                          // navigator.clipboard.writeText rejects (e.g. in
                          // insecure contexts or when the user denies
                          // permission). Catch the rejection so it does not
                          // surface as an unhandled promise rejection in the
                          // console; surface a toast the user can act on.
                          const p = navigator.clipboard?.writeText(m.s3_key);
                          if (p) {
                            p.then(
                              () => toast.push('info', 'Copied s3 key to clipboard'),
                              (e) =>
                                toast.push(
                                  'error',
                                  `Copy failed: ${e instanceof Error ? e.message : String(e)}`,
                                ),
                            );
                          } else {
                            toast.push('error', 'Clipboard API unavailable in this context');
                          }
                        }}
                        title="Click to copy"
                      >
                        {m.s3_key}
                      </button>
                    </TD>
                    <TD className="capitalize">{m.provider}</TD>
                    <TD className="capitalize">{m.sport}</TD>
                    <TD className="font-mono text-xs">{m.endpoint_type}</TD>
                    <TD className="tabular-nums">{m.http_status ?? '—'}</TD>
                    <TD className="text-right tabular-nums">{formatBytes(m.response_bytes)}</TD>
                    <TD>{formatRelative(m.collected_at)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState icon={HardDrive} title="No objects match the current filters" />
          )}
        </CardContent>
      </Card>
    </>
  );
}
